<template>
  <div>
    <PageHeadline title="お知らせ" text-align="left" />
    <div class="notification__tab-area">
      <TabSwitches
        :items="tabItems"
        :active-tab="activeTab"
        @change-active-tab="onChangeActiveTab"
      />
      <div class="notification__tab-contents">
        <TabContents :items="tabItems" :active-tab="activeTab">
          <!-- [index]が使用できないので、at(index)を使用 -->
          <template #[tabItems.at(0).id]>
            <ForYouTabBody
              :page-hash="`#${activeTab}`"
              :logged-in="loggedIn"
              :registration-path="registrationPath"
              :login-path="loginPath"
              :is-active="isForYouTabActive"
              @personal-notification-loaded="onPersonalNotificationLoaded"
            />
          </template>
          <template #[tabItems.at(1).id]>
            <NewsTabBody @news-loaded="onNewsLoaded" />
          </template>
          <template #[tabItems.at(2).id]>
            <SpecialsTabBody :is-active="isSpecalTabActive" />
          </template>
        </TabContents>
      </div>
    </div>
    <div class="notification__footer">
      <div class="notification__back-to-root">
        <a
          href="/"
          class="notification__back-to-root__link tl-link-chevron-back--left"
        >
          gifteeトップへ戻る
        </a>
      </div>
      <div v-if="showLinkToTop" class="notification__back-to-top">
        <span class="text-link-to-top" @click.self="onClickLinkToTop">
          ページトップへ ▲
        </span>
      </div>
    </div>
  </div>
</template>
<script>
import PageHeadline from '../../molecules/headlines/PageHeadline.vue'
import TabSwitches from '../../molecules/tab/TabSwitches.vue'
import TabContents from '../../molecules/tab/TabContents.vue'
import SpecialsTabBody from './SpecialsTabBody.vue'
import NewsTabBody from './NewsTabBody.vue'
import ForYouTabBody from './ForYouTabBody.vue'
import url from 'url'
import axios from 'axios'

export default {
  components: {
    PageHeadline,
    TabSwitches,
    TabContents,
    SpecialsTabBody,
    NewsTabBody,
    ForYouTabBody
  },
  props: {
    loggedIn: {
      type: Boolean,
      required: false,
      default: false
    },
    hasForYouUnread: {
      type: Boolean,
      required: false,
      default: false
    },
    hasNewsUnread: {
      type: Boolean,
      required: false,
      default: false
    },
    hasSpecialUnread: {
      type: Boolean,
      required: false,
      default: false
    },
    registrationPath: {
      type: String,
      required: false,
      default: ''
    },
    loginPath: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    const TAB_ITEMS = [
      { id: 'for-you', title: 'あなた宛', unread: this.hasForYouUnread },
      { id: 'news', title: 'ニュース', unread: this.hasNewsUnread },
      { id: 'special', title: '特集・イベント', unread: this.hasSpecialUnread }
    ]
    return {
      tabItems: TAB_ITEMS,
      activeTab: TAB_ITEMS[0].id,
      isEmptyPersonalNotifications: true,
      isEmptyNews: true
    }
  },
  computed: {
    isForYouTabActive() {
      return this.activeTab === this.tabItems[0].id
    },
    isNewsTabActive() {
      return this.activeTab === this.tabItems[1].id
    },
    isSpecalTabActive() {
      return this.activeTab === this.tabItems[2].id
    },
    showLinkToTop() {
      if (this.isForYouTabActive) {
        return !this.isEmptyPersonalNotifications
      } else if (this.isNewsTabActive) {
        return !this.isEmptyNews
      } else if (this.isSpecalTabActive) {
        return true
      }
      return false
    }
  },
  created() {
    this.changeActiveTabFromUrlHash()

    // NOTE: ブラウザバック・フォワード時の更新処理
    // タブの情報が正しく表示されるよう画面をリロードする
    window.onpopstate = function () {
      location.reload()
    }.bind(this)
  },
  methods: {
    async changeActiveTabFromUrlHash() {
      const urlHash = url.parse(location.href).hash
      if (!urlHash) {
        return
      }

      const tab = urlHash.substring(1)
      if (this.tabItems.some(tabItem => tabItem.id === tab)) {
        this.activeTab = tab
      } else {
        this.activeTab = this.tabItems[0].id
      }

      this.readActiveTab()
      await this.updateTabAccessedAt()
    },
    async onChangeActiveTab(value) {
      this.activeTab = value
      this.readActiveTab()
      history.pushState(
        '',
        '',
        `${url.parse(location.href).pathname}#${this.activeTab}`
      )
      await this.updateTabAccessedAt()
    },
    readActiveTab() {
      this.tabItems.forEach((item, index) => {
        if (index !== 0 && item.id === this.activeTab) {
          item.unread = false
        }
      })
    },
    onPersonalNotificationLoaded(
      hasForYouUnread,
      isEmptyPersonalNotifications
    ) {
      this.tabItems[0].unread = hasForYouUnread
      this.isEmptyPersonalNotifications = isEmptyPersonalNotifications
    },
    onNewsLoaded(isEmptyNews) {
      this.isEmptyNews = isEmptyNews
    },
    async updateTabAccessedAt() {
      if (this.isForYouTabActive) {
        return
      }
      try {
        const params = {}
        if (this.isNewsTabActive) {
          params['news_tab_last_accessed_at'] = new Date()
        }
        if (this.isSpecalTabActive) {
          params['special_tab_last_accessed_at'] = new Date()
        }
        await axios.patch('/api/v1/users/@me/notifications/update', {
          notification: params
        })
      } catch (error) {
        // TODO: Rails 側でエラーが発生した際にフロント側でどのような処理をするかを検討し、ここに実装する
        if (
          !error.response ||
          !error.response.status ||
          !error.response.statusText
        ) {
          console.log(`Error :${error}`)
          return
        }
        const { status, statusText } = error.response
        console.log(`Error! HTTP Status: ${status} ${statusText}`)
        const errorMessage = error.response.data.message
        if (errorMessage) {
          console.log(`Error message: ${errorMessage}`)
        }
      }
    },
    onClickLinkToTop() {
      this.scrollToTop()
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }
}
</script>
<style lang="scss" scoped>
.notification {
  &__tab-area {
    margin-top: 16px;
    background-color: $white;
  }

  &__back-to-root {
    padding: 24px 0 56px 8px;
    margin: auto;
    font-size: 1.6rem;
    line-height: 1.6;
    text-align: left;
    max-width: 840px;

    &__link {
      color: $link;
      background-color: transparent;
    }
  }

  &__back-to-top {
    margin: auto;
    padding: 16px 0;
    background-color: $gray-2;

    .text-link-to-top {
      display: inline;
    }
  }
}
</style>
